import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router"
import {
  ChevronLeft,
  ChevronRight,
  Download,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react"

import {
  type CreosUniqueBatch,
  type CreosUniqueJob,
  useCreateCreosUniqueJob,
  useCreosUniqueBatches,
  useRestartCreosUniqueJob,
} from "@/api/hooks/creos"
import { ApiError } from "@/api/client"
import { useAuth } from "@/auth/AuthProvider"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { usePageTitle } from "@/layouts/pageChrome"
import { triggerAuthenticatedDownload } from "@/lib/download"
import { cn } from "@/lib/utils"

const MAX_IMAGE_BYTES = 20 * 1024 * 1024
const MAX_VIDEO_BYTES = 100 * 1024 * 1024
const MAX_CAROUSEL_IMAGES = 10
const MAX_VIDEOS = 10
const MAX_CAROUSELS = 10

function batchZipName(batchId: string): string {
  return `creos-batch-${batchId.replace(/-/g, "").slice(0, 8)}.zip`
}

function batchHref(batch: CreosUniqueBatch): string {
  if (batch.batch_id) return `/creos/uniquify/batch/${batch.batch_id}`
  if (batch.solo_public_id) return `/creos/uniquify/${batch.solo_public_id}`
  return "/creos/uniquify"
}

function batchIdLabel(batch: CreosUniqueBatch): string {
  if (batch.batch_id) {
    return batch.batch_id.replace(/-/g, "").slice(0, 8)
  }
  return batch.solo_public_id || "—"
}

function batchTypeLabel(batch: CreosUniqueBatch): string {
  if (batch.media_kinds.length === 1 && batch.job_count === 1) {
    return batch.media_kinds[0]
  }
  if (batch.media_kinds.length) {
    return `${batch.job_count} · ${batch.media_kinds.join(" · ")}`
  }
  return `${batch.job_count} item${batch.job_count === 1 ? "" : "s"}`
}

function batchProgressLabel(batch: CreosUniqueBatch): string {
  if (batch.job_count <= 1) return ""
  return `${batch.completed_count}/${batch.job_count} done`
}

function isBatchZipReady(batch: CreosUniqueBatch): boolean {
  return (
    (batch.status === "completed" || batch.status === "partial") &&
    batch.completed_count > 0 &&
    Boolean(batch.download_url)
  )
}

/** Distinct left-edge accents so carousel batches stay visually separate. */
const CAROUSEL_ACCENTS = [
  "border-l-[#3db8a0]",
  "border-l-[#d4a017]",
  "border-l-[#7488e8]",
  "border-l-[#e07a5f]",
  "border-l-[#81b29a]",
  "border-l-[#9b7ebd]",
] as const

type QueueKind = "video" | "carousel"

interface CarouselDraft {
  id: string
  images: File[]
}

function newCarouselDraft(): CarouselDraft {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    images: [],
  }
}

interface QueueItem {
  key: string
  kind: QueueKind
  files: File[]
  label: string
}

function isVideoFile(file: File): boolean {
  if (file.type.startsWith("video/")) return true
  return /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(file.name)
}

function isImageFile(file: File): boolean {
  if (file.type.startsWith("image/")) return true
  return /\.(jpe?g|png|webp|bmp|tiff?|gif)$/i.test(file.name)
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "completed":
      return "bg-emerald-500/20 text-emerald-300"
    case "failed":
      return "bg-red-500/20 text-red-300"
    case "partial":
      return "bg-orange-500/20 text-orange-300"
    case "processing":
      return "bg-amber-500/20 text-amber-300"
    case "pending":
      return "bg-sky-500/20 text-sky-300"
    default:
      return "bg-white/10 text-[#aaa]"
  }
}

function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function prettyAspect(w: number, h: number): string {
  if (!w || !h) return ""
  const ratio = w / h
  const buckets: { label: string; r: number }[] = [
    { label: "9:16", r: 9 / 16 },
    { label: "1:1", r: 1 },
    { label: "4:5", r: 4 / 5 },
    { label: "16:9", r: 16 / 9 },
    { label: "4:3", r: 4 / 3 },
  ]
  for (const b of buckets) {
    if (Math.abs(ratio - b.r) / b.r < 0.03) return b.label
  }
  const g = gcd(w, h)
  return `${Math.round(w / g)}:${Math.round(h / g)}`
}

function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a
}

/**
 * Object URL for a local file, revoked when the file changes or on unmount.
 * The effect owns create/revoke; the URL is handed to state from a microtask
 * (skipped if the effect was already cleaned up) instead of synchronously.
 */
function useObjectUrl(file: File): string {
  const [url, setUrl] = useState("")

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file)
    let cancelled = false
    queueMicrotask(() => {
      if (!cancelled) setUrl(objectUrl)
    })
    return () => {
      cancelled = true
      URL.revokeObjectURL(objectUrl)
    }
  }, [file])

  return url
}

function VideoPreviewCard({
  file,
  index,
  onRemove,
}: {
  file: File
  index: number
  onRemove: () => void
}) {
  const url = useObjectUrl(file)
  const [ratio, setRatio] = useState(9 / 16)
  const [aspectLabel, setAspectLabel] = useState("")

  useEffect(() => {
    if (!url) return
    const probe = document.createElement("video")
    probe.preload = "metadata"
    probe.src = url
    probe.onloadedmetadata = () => {
      const w = probe.videoWidth || 0
      const h = probe.videoHeight || 1
      if (!w || !h) return
      setRatio(w / h)
      setAspectLabel(prettyAspect(w, h))
    }
  }, [url])

  return (
    <div className="relative w-[180px] overflow-hidden rounded-md border border-[#3a3a3a] bg-black">
      {url ? (
        <div className="w-full bg-black" style={{ aspectRatio: `${ratio}` }}>
          <video
            src={url}
            controls
            muted
            playsInline
            preload="metadata"
            className="block h-full w-full object-contain"
          />
        </div>
      ) : null}
      <span className="absolute left-1.5 top-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
        #{index + 1}
      </span>
      {aspectLabel ? (
        <span className="absolute right-8 top-1.5 rounded bg-[#7a8aef]/85 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {aspectLabel}
        </span>
      ) : null}
      <button
        type="button"
        className="absolute right-1.5 top-1.5 rounded bg-black/70 p-1 text-white hover:bg-red-900/90"
        onClick={onRemove}
        aria-label={`Remove video ${index + 1}`}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
      <div className="truncate bg-[#242424] px-2 py-1 text-[11px] text-white">
        {file.name}
        <span className="ml-1 text-[#888]">({formatBytes(file.size)})</span>
      </div>
    </div>
  )
}

function ImagePreviewCard({
  file,
  index,
  total,
  onMove,
  onRemove,
}: {
  file: File
  index: number
  total: number
  onMove: (to: number) => void
  onRemove: () => void
}) {
  const url = useObjectUrl(file)

  return (
    <div className="relative w-[148px] shrink-0 overflow-hidden rounded-md border border-[#3a3a3a] bg-black">
      {url ? (
        <div className="h-[260px] w-full bg-black">
          <img
            src={url}
            alt={file.name}
            className="block h-full w-full object-contain"
          />
        </div>
      ) : (
        <div className="h-[260px] w-full bg-[#1a1a1a]" />
      )}
      <span className="absolute left-1.5 top-1.5 rounded bg-black/75 px-1.5 py-0.5 text-[11px] font-medium text-white">
        #{index + 1}
      </span>
      <div className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-1 bg-black/75 px-1 py-1">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            disabled={index === 0}
            className="rounded p-1 text-white hover:bg-white/10 disabled:opacity-30"
            onClick={() => onMove(index - 1)}
            aria-label="Move left"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            className="rounded p-1 text-white hover:bg-white/10 disabled:opacity-30"
            onClick={() => onMove(index + 1)}
            aria-label="Move right"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <button
          type="button"
          className="rounded p-1 text-red-300 hover:bg-red-900/50"
          onClick={onRemove}
          aria-label={`Remove image ${index + 1}`}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  )
}

function formatDuration(sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return null
  const s = Math.round(sec)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`
  const h = Math.floor(m / 60)
  const rm = m % 60
  return rm ? `${h}h ${rm}m` : `${h}h`
}

/** Prefer ApiError.message — `detail` is often a raw object (`{detail:…}`). */
function formatQueueError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return "Failed to queue"
}

export default function CreosUniquifyPage() {
  usePageTitle("", "CreOS — Uniquify")
  const { user } = useAuth()
  const create = useCreateCreosUniqueJob()
  const restart = useRestartCreosUniqueJob()
  const [restartError, setRestartError] = useState<string | null>(null)
  const videoInputRef = useRef<HTMLInputElement | null>(null)
  const carouselInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const [videos, setVideos] = useState<File[]>([])
  const [carousels, setCarousels] = useState<CarouselDraft[]>(() => [
    newCarouselDraft(),
  ])
  const [formError, setFormError] = useState<string | null>(null)
  const [submitSummary, setSubmitSummary] = useState<{
    created: CreosUniqueJob[]
    failed: string[]
    batchId: string | null
  } | null>(null)
  const [searchInput, setSearchInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [myAdsOnly, setMyAdsOnly] = useState(true)

  const canToggleMyAds =
    user?.role === "admin" || user?.role === "team_leader"

  useEffect(() => {
    const t = window.setTimeout(() => setSearchQuery(searchInput.trim()), 300)
    return () => window.clearTimeout(t)
  }, [searchInput])

  const batchesQuery = useCreosUniqueBatches(
    {
      limit: 50,
      offset: 0,
      q: searchQuery || undefined,
      mine: canToggleMyAds ? myAdsOnly : true,
    },
    {
      refetchInterval: (query) => {
        const items = query.state.data?.items ?? []
        const busy = items.some(
          (b) =>
            b.status === "pending" ||
            b.status === "processing" ||
            b.status === "partial",
        )
        return busy ? 5000 : false
      },
    },
  )

  const queueItems = useMemo<QueueItem[]>(() => {
    const videoItems = videos.map((file, idx) => ({
      key: `video-${idx}-${file.name}-${file.size}`,
      kind: "video" as const,
      files: [file],
      label: file.name,
    }))
    const carouselItems = carousels
      .filter((c) => c.images.length > 0)
      .map((carousel, idx) => ({
        key: `carousel-${carousel.id}`,
        kind: "carousel" as const,
        files: carousel.images,
        label: `Carousel ${idx + 1} (${carousel.images.length} image${carousel.images.length === 1 ? "" : "s"})`,
      }))
    return [...videoItems, ...carouselItems]
  }, [videos, carousels])

  function resetSummaryError() {
    setFormError(null)
    setSubmitSummary(null)
  }

  function appendVideos(next: File[]) {
    const accepted = next.filter(isVideoFile)
    if (!accepted.length) return
    const room = MAX_VIDEOS - videos.length
    if (room <= 0) {
      setFormError(`You can upload at most ${MAX_VIDEOS} videos per batch`)
      return
    }
    if (accepted.length > room) {
      setFormError(`Only ${room} more video(s) allowed (max ${MAX_VIDEOS})`)
    } else {
      setFormError(null)
    }
    setSubmitSummary(null)
    setVideos((prev) => [...prev, ...accepted.slice(0, Math.max(0, MAX_VIDEOS - prev.length))])
  }

  function addCarouselDraft() {
    if (carousels.length >= MAX_CAROUSELS) {
      setFormError(`You can upload at most ${MAX_CAROUSELS} carousels per batch`)
      return
    }
    setFormError(null)
    setSubmitSummary(null)
    setCarousels((prev) => [...prev, newCarouselDraft()])
  }

  function removeCarouselDraft(id: string) {
    setCarousels((prev) => prev.filter((c) => c.id !== id))
    delete carouselInputRefs.current[id]
    resetSummaryError()
  }

  function appendCarouselImages(id: string, next: File[]) {
    const accepted = next.filter(isImageFile)
    setCarousels((prev) =>
      prev.map((carousel) => {
        if (carousel.id !== id) return carousel
        return {
          ...carousel,
          images: [...carousel.images, ...accepted].slice(0, MAX_CAROUSEL_IMAGES),
        }
      }),
    )
    setFormError(null)
  }

  function reorderCarouselImages(id: string, from: number, to: number) {
    setCarousels((prev) =>
      prev.map((carousel) => {
        if (carousel.id !== id) return carousel
        if (to < 0 || to >= carousel.images.length) return carousel
        const images = carousel.images.slice()
        const [moved] = images.splice(from, 1)
        if (!moved) return carousel
        images.splice(to, 0, moved)
        return { ...carousel, images }
      }),
    )
  }

  function validateQueue(): string | null {
    if (!queueItems.length) {
      return "Add at least one video or carousel"
    }
    if (videos.length > MAX_VIDEOS) {
      return `At most ${MAX_VIDEOS} videos per batch`
    }
    const filledCarousels = carousels.filter((c) => c.images.length > 0).length
    if (filledCarousels > MAX_CAROUSELS || carousels.length > MAX_CAROUSELS) {
      return `At most ${MAX_CAROUSELS} carousels per batch`
    }
    for (const [idx, carousel] of carousels.entries()) {
      if (carousel.images.length > MAX_CAROUSEL_IMAGES) {
        return `Carousel ${idx + 1} can contain at most ${MAX_CAROUSEL_IMAGES} images`
      }
    }
    for (const item of queueItems) {
      for (const file of item.files) {
        const limit = item.kind === "video" ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES
        if (file.size > limit) {
          return `${file.name} is too large (max ${limit / (1024 * 1024)} MB)`
        }
      }
    }
    return null
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const err = validateQueue()
    if (err) {
      setFormError(err)
      return
    }

    const created: CreosUniqueJob[] = []
    const failed: string[] = []
    const batchId = crypto.randomUUID()

    try {
      for (const item of queueItems) {
        try {
          const job = await create.mutateAsync({ files: item.files, batchId })
          created.push(job)
        } catch (err) {
          failed.push(`${item.label}: ${formatQueueError(err)}`)
        }
      }

      setSubmitSummary({
        created,
        failed,
        batchId: created.length > 0 ? batchId : null,
      })

      if (created.length > 0 && failed.length === 0) {
        setVideos([])
        setCarousels([newCarouselDraft()])
      }
    } catch (err) {
      setFormError(`Failed to queue ads: ${formatQueueError(err)}`)
    }
  }

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit}
        className="space-y-5 rounded-xl border border-[#4a4a4a] bg-[#2d2d2d] p-6"
      >
        {/* Videos — mockup A stacked section */}
        <div className="space-y-3">
          <h2 className="text-base font-semibold text-white">
            Videos
            <span className="ml-1.5 text-xs font-normal text-[#888]">
              · max {MAX_VIDEOS} · {MAX_VIDEO_BYTES / (1024 * 1024)} MB
            </span>
          </h2>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              appendVideos(Array.from(e.target.files ?? []))
              e.target.value = ""
            }}
          />
          {videos.length === 0 ? (
            <button
              type="button"
              onClick={() => videoInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                appendVideos(Array.from(e.dataTransfer.files ?? []))
              }}
              className="flex w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[#555] bg-[#292929] px-4 py-8 transition-colors hover:border-[#7488e8]/60 hover:bg-[#2a2a32]"
            >
              <span className="rounded-md bg-[#7488e8] px-4 py-2 text-sm font-medium text-white">
                Add videos
              </span>
              <span className="text-xs text-[#777]">
                Drop files here or click to browse
              </span>
            </button>
          ) : (
            <div className="flex flex-wrap items-start gap-3">
              {videos.map((file, idx) => (
                <VideoPreviewCard
                  key={`${file.name}-${idx}-${file.size}`}
                  file={file}
                  index={idx}
                  onRemove={() =>
                    setVideos((prev) => prev.filter((_, i) => i !== idx))
                  }
                />
              ))}
              {videos.length < MAX_VIDEOS ? (
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    appendVideos(Array.from(e.dataTransfer.files ?? []))
                  }}
                  className="flex w-[180px] flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[#555] bg-[#292929] text-[#888] transition-colors hover:border-[#7488e8]/70 hover:bg-[#2a2a32] hover:text-[#c5ceff]"
                  style={{ aspectRatio: "9 / 16" }}
                  aria-label="Add more videos"
                >
                  <span className="text-3xl font-light leading-none text-[#7488e8]">
                    +
                  </span>
                  <span className="text-xs font-medium">Add more</span>
                  <span className="px-2 text-center text-[10px] text-[#666]">
                    {MAX_VIDEOS - videos.length} left
                  </span>
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/* Carousels — separate section, accent stripe per batch */}
        <div className="space-y-3 border-t border-[#3a3a3a] pt-5">
          <h2 className="text-base font-semibold text-white">
            Carousels
            <span className="ml-1.5 text-xs font-normal text-[#888]">
              · max {MAX_CAROUSELS} · 1–{MAX_CAROUSEL_IMAGES} images each
            </span>
          </h2>

          {carousels.length ? (
            <div className="space-y-3">
              {carousels.map((carousel, idx) => {
                const accent =
                  CAROUSEL_ACCENTS[idx % CAROUSEL_ACCENTS.length]
                return (
                  <div
                    key={carousel.id}
                    className={cn(
                      "space-y-3 rounded-lg border border-[#444] border-l-4 bg-[#2a2a2a] p-4",
                      accent,
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-medium text-white">
                        Carousel {idx + 1}
                        <span className="ml-1.5 font-normal text-[#888]">
                          · {carousel.images.length} slide
                          {carousel.images.length === 1 ? "" : "s"}
                        </span>
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          ref={(el) => {
                            carouselInputRefs.current[carousel.id] = el
                          }}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            appendCarouselImages(
                              carousel.id,
                              Array.from(e.target.files ?? []),
                            )
                            e.target.value = ""
                          }}
                        />
                        <Button
                          type="button"
                          size="sm"
                          className="bg-[#7488e8] hover:bg-[#687bda]"
                          onClick={() =>
                            carouselInputRefs.current[carousel.id]?.click()
                          }
                        >
                          Add images
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-[#555] bg-transparent text-[#ccc] hover:bg-[#333] hover:text-white"
                          onClick={() => removeCarouselDraft(carousel.id)}
                        >
                          <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                          Remove
                        </Button>
                      </div>
                    </div>
                    {carousel.images.length ? (
                      <div className="flex flex-wrap items-start gap-3">
                        {carousel.images.map((file, imageIdx) => (
                          <ImagePreviewCard
                            key={`${file.name}-${imageIdx}-${file.size}`}
                            file={file}
                            index={imageIdx}
                            total={carousel.images.length}
                            onMove={(to) =>
                              reorderCarouselImages(
                                carousel.id,
                                imageIdx,
                                to,
                              )
                            }
                            onRemove={() =>
                              setCarousels((prev) =>
                                prev.map((c) =>
                                  c.id === carousel.id
                                    ? {
                                        ...c,
                                        images: c.images.filter(
                                          (_, i) => i !== imageIdx,
                                        ),
                                      }
                                    : c,
                                ),
                              )
                            }
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#777]">
                        No images yet — add slides for this carousel.
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          ) : null}

          {carousels.length < MAX_CAROUSELS ? (
            <button
              type="button"
              onClick={addCarouselDraft}
              className="w-full rounded-lg border border-dashed border-[#7488e8]/70 bg-[#7488e8]/10 py-3 text-sm font-medium text-[#c5ceff] hover:border-[#7488e8] hover:bg-[#7488e8]/20 hover:text-white"
            >
              {carousels.length === 0
                ? "+ Add carousel"
                : "+ Add another carousel"}
            </button>
          ) : null}
        </div>

        {/* Submit zone — dominant CTA, separate from add/remove controls */}
        <div className="sticky bottom-0 z-10 -mx-6 border-t border-[#555] bg-[#252525] px-6 py-4">
          <div className="space-y-3 rounded-lg border border-[#7488e8]/40 bg-[#2a2d3a] p-4">
            <p className="text-sm text-[#ccc]">
              Queue ready:{" "}
              <span className="font-semibold text-white">
                {queueItems.length}
              </span>{" "}
              ad{queueItems.length === 1 ? "" : "s"}
              {queueItems.length > 0 ? (
                <span className="text-[#888]">
                  {" "}
                  ({videos.length} video{videos.length === 1 ? "" : "s"} +{" "}
                  {carousels.filter((c) => c.images.length > 0).length} carousel
                  {carousels.filter((c) => c.images.length > 0).length === 1
                    ? ""
                    : "s"}
                  )
                </span>
              ) : null}
            </p>
            <Button
              type="submit"
              disabled={create.isPending || queueItems.length === 0}
              className="h-11 w-full text-base font-semibold bg-[#7488e8] hover:bg-[#687bda] disabled:opacity-40"
            >
              {create.isPending ? "Queueing…" : "Queue all"}
            </Button>
          </div>
        </div>

        {formError ? <p className="text-sm text-red-300">{formError}</p> : null}
        {submitSummary ? (
          <div className="space-y-2 rounded-md border border-[#4a4a4a] bg-[#292929] p-3 text-sm">
            <p className="text-[#ddd]">
              Queued:{" "}
              <span className="text-emerald-300">
                {submitSummary.created.length}
              </span>{" "}
              job(s), failed:{" "}
              <span
                className={cn(
                  submitSummary.failed.length ? "text-red-300" : "text-[#ddd]",
                )}
              >
                {submitSummary.failed.length}
              </span>
            </p>
            {submitSummary.failed.length ? (
              <ul className="list-disc space-y-1 pl-5 text-xs text-red-300">
                {submitSummary.failed.map((msg, idx) => (
                  <li key={`${msg}-${idx}`}>{msg}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </form>

      {/* Jobs table — mockup B style */}
      <div className="overflow-hidden rounded-xl border border-[#4a4a4a] bg-[#2d2d2d]">
        <div className="flex items-center gap-3 border-b border-[#4a4a4a] px-4 py-3">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by ID, filename, or creator…"
            className="min-w-0 flex-1 rounded-md border border-[#555] bg-[#1f1f1f] px-3 py-2 text-sm text-white placeholder:text-[#777] focus:border-[#7488e8] focus:outline-none"
          />
          {canToggleMyAds ? (
            <label className="ml-auto flex shrink-0 cursor-pointer select-none items-center gap-2 text-sm text-[#ccc]">
              <Checkbox
                checked={myAdsOnly}
                onCheckedChange={(value) => setMyAdsOnly(value === true)}
                className="border-[#666] data-[state=checked]:border-[#7488e8] data-[state=checked]:bg-[#7488e8]"
              />
              My ads
            </label>
          ) : null}
        </div>
        {restartError ? (
          <p className="border-b border-[#4a4a4a] px-4 py-2 text-sm text-red-300">
            {restartError}
          </p>
        ) : null}
        {batchesQuery.isLoading ? (
          <p className="p-6 text-sm text-[#aaa]">Loading jobs…</p>
        ) : batchesQuery.error ? (
          <p className="p-6 text-sm text-red-300">
            Failed to load jobs: {batchesQuery.error.message}
          </p>
        ) : !batchesQuery.data?.items.length ? (
          <p className="p-6 text-sm text-[#aaa]">
            {searchQuery
              ? `No jobs match “${searchQuery}”.`
              : myAdsOnly && canToggleMyAds
                ? "No uniquify jobs from you yet. Upload a creative to get started."
                : user?.role === "team_leader"
                  ? "No uniquify jobs from you or your team yet."
                  : "No uniquify jobs yet. Upload a creative to get started."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-[#4a4a4a] bg-[#3a3a3a] text-[#aaa]">
                <tr>
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Files</th>
                  <th className="px-4 py-3 font-medium">Created by</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium text-right">Download</th>
                </tr>
              </thead>
              <tbody>
                {batchesQuery.data.items.map((batch) => {
                  const duration = formatDuration(batch.duration_sec)
                  const progress = batchProgressLabel(batch)
                  const href = batchHref(batch)
                  const rowKey = batch.batch_id || batch.solo_public_id || href
                  const showBatchZip = Boolean(batch.batch_id)
                  const batchZipReady = showBatchZip && isBatchZipReady(batch)
                  const soloCompleted =
                    !batch.batch_id &&
                    batch.status === "completed" &&
                    batch.download_url
                  const soloFailed =
                    !batch.batch_id &&
                    batch.status === "failed" &&
                    batch.solo_public_id
                  return (
                    <tr
                      key={rowKey}
                      className="border-b border-[#3a3a3a] hover:bg-[#333]"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={href}
                          className="font-mono text-sm font-medium text-[#8b9aff] hover:underline"
                        >
                          {batchIdLabel(batch)}
                        </Link>
                        {batch.source === "campaign_form" ? (
                          <p className="mt-0.5 text-[11px] text-amber-300/90">
                            From Create Campaign
                          </p>
                        ) : null}
                        {batch.job_count > 1 ? (
                          <p className="mt-0.5 text-[11px] text-[#888]">
                            {batch.job_count} ads
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                            statusBadgeClass(batch.status),
                          )}
                        >
                          {batch.status}
                        </span>
                        {progress ? (
                          <p className="mt-1 text-xs text-[#888]">{progress}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 capitalize text-white">
                        {batchTypeLabel(batch)}
                      </td>
                      <td className="px-4 py-3 text-[#ccc]">
                        {batch.source_preview.join(", ") || "—"}
                      </td>
                      <td className="px-4 py-3 text-[#ccc]">
                        {batch.created_by_username || "—"}
                      </td>
                      <td className="px-4 py-3 text-[#aaa]">
                        {batch.created_at
                          ? new Date(batch.created_at).toLocaleString()
                          : "—"}
                        {duration ? (
                          <span className="text-[#888]"> ({duration})</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {showBatchZip ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              aria-disabled={!batchZipReady}
                              title={
                                batchZipReady ? undefined : "Wait until finished"
                              }
                              className={cn(
                                "border-[#555] bg-transparent text-white hover:bg-[#333]",
                                !batchZipReady &&
                                  "cursor-not-allowed opacity-40 hover:bg-transparent",
                              )}
                              onClick={() => {
                                if (!batchZipReady || !batch.download_url) return
                                triggerAuthenticatedDownload(
                                  batch.download_url,
                                  batchZipName(batch.batch_id!),
                                )
                              }}
                            >
                              <Download
                                className="mr-1.5 h-3.5 w-3.5"
                                aria-hidden
                              />
                              Batch ZIP
                            </Button>
                          ) : null}
                          {soloCompleted ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="border-[#555] bg-transparent text-white hover:bg-[#333]"
                              onClick={() => {
                                triggerAuthenticatedDownload(
                                  batch.download_url!,
                                  `creos-unique-${batch.solo_public_id}`,
                                )
                              }}
                            >
                              <Download
                                className="mr-1.5 h-3.5 w-3.5"
                                aria-hidden
                              />
                              Download
                            </Button>
                          ) : null}
                          {soloFailed ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={
                                restart.isPending &&
                                restart.variables === batch.solo_public_id
                              }
                              className="border-[#555] bg-transparent text-white hover:bg-[#333]"
                              onClick={() => {
                                setRestartError(null)
                                restart.mutate(batch.solo_public_id!, {
                                  onError: (err) => {
                                    setRestartError(
                                      err instanceof Error
                                        ? err.message
                                        : "Failed to restart",
                                    )
                                  },
                                })
                              }}
                            >
                              <RotateCcw
                                className="mr-1.5 h-3.5 w-3.5"
                                aria-hidden
                              />
                              {restart.isPending &&
                              restart.variables === batch.solo_public_id
                                ? "Restarting…"
                                : "Restart"}
                            </Button>
                          ) : null}
                          {!showBatchZip && !soloCompleted && !soloFailed ? (
                            <span className="text-xs text-[#666]">—</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
